import { format, getMonth, getYear } from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "../../assets/icons";

const isMonthOutOfRange = (year, month, minDate, maxDate) => {
  if (!minDate && !maxDate) return false;
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0, 23, 59, 59, 999);
  if (minDate && last < minDate) return true;
  if (maxDate && first > maxDate) return true;
  return false;
};

const buildYearList = (currentYear, minDate, maxDate) => {
  const yMin = minDate ? getYear(minDate) : currentYear - 8;
  const yMax = maxDate ? getYear(maxDate) : currentYear + 8;
  const from = Math.min(yMin, currentYear);
  const to = Math.max(yMax, currentYear);
  const out = [];
  for (let y = from; y <= to; y += 1) out.push(y);
  return out;
};

const iconBtnClass =
  "workfully-datepicker__nav-btn inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-100 bg-white text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-workfully-600";

/**
 * @param {{ minDate?: Date, maxDate?: Date, useShortMonth?: boolean, monthsShown?: number, betweenNavAndDayNames?: unknown }} options
 * @returns {(props: object) => JSX.Element} render prop pour `renderCustomHeader` (react-datepicker)
 */
export const createDatePickerCustomHeader = ({
  minDate,
  maxDate,
  useShortMonth = false,
  monthsShown = 1,
  /** Rendu entre la barre mois/année et la ligne des jours (LU, MA…). Masqué sur les mois suivants si monthsShown > 1. */
  betweenNavAndDayNames = null,
} = {}) => {
  return function DatePickerCustomHeader({
    monthDate,
    changeMonth,
    changeYear,
    decreaseMonth,
    increaseMonth,
    customHeaderCount = 0,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) {
    const y = getYear(monthDate);
    const m = getMonth(monthDate);
    const monthPattern = useShortMonth ? "LLL" : "LLLL";
    const years = buildYearList(y, minDate, maxDate);

    const showPrev = customHeaderCount === 0;
    const showNext = customHeaderCount === monthsShown - 1;

    return (
      <>
        <div className="workfully-datepicker__header">
          {showPrev ? (
            <button
              type="button"
              className={iconBtnClass}
              onClick={decreaseMonth}
              disabled={prevMonthButtonDisabled}
              aria-label="Mois précédent"
            >
              <ChevronLeft wh={20} color="#374151" />
            </button>
          ) : (
            <div className="h-9 w-9 shrink-0" aria-hidden="true" />
          )}

          <div className="workfully-datepicker__header-selects">
            <select
              className="react-datepicker__month-select"
              value={m}
              onChange={(e) => changeMonth(Number(e.target.value))}
              aria-label="Mois"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option
                  key={i}
                  value={i}
                  disabled={isMonthOutOfRange(y, i, minDate, maxDate)}
                >
                  {format(new Date(2000, i, 1), monthPattern, { locale: fr })}
                </option>
              ))}
            </select>
            <select
              className="react-datepicker__year-select"
              value={y}
              onChange={(e) => changeYear(Number(e.target.value))}
              aria-label="Année"
            >
              {years.map((yr) => (
                <option key={yr} value={yr}>
                  {yr}
                </option>
              ))}
            </select>
          </div>

          {showNext ? (
            <button
              type="button"
              className={iconBtnClass}
              onClick={increaseMonth}
              disabled={nextMonthButtonDisabled}
              aria-label="Mois suivant"
            >
              <ChevronRight wh={20} color="#374151" />
            </button>
          ) : (
            <div className="h-9 w-9 shrink-0" aria-hidden="true" />
          )}
        </div>
        {betweenNavAndDayNames != null && customHeaderCount === 0
          ? betweenNavAndDayNames
          : null}
      </>
    );
  };
};

export default createDatePickerCustomHeader;
